import React, { FC, useState } from 'react';
import { Button, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import moment from 'moment';

import * as paths from 'paths';
import {
  CreateClientInvitationInput,
  Ordering,
  OrganicInsightsAdvancedFilters,
  OrganicInsightsReportFilter,
  RegistrationStatus,
  ReportNames,
  useCreateClientInvitationMutation,
  useGetOrganicInsightsReportQuery,
  UserType,
} from 'api';
import { CheckedIds } from 'types/app';
import { useNotify, useProtectedClient } from 'hooks';

import ExportData from 'features/admin/components/shared/ExportData';
import { CompaniesFilter, OrganicInsightsFilters, PeopleFilter } from '../types';

interface Props {
  registration_status?: RegistrationStatus;
  checkedIds?: CheckedIds;
  filters?: OrganicInsightsFilters;
  advanced_filters?: OrganicInsightsAdvancedFilters;
}

const Header: FC<Props> = ({ checkedIds, registration_status, filters, advanced_filters }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const { pathname } = useLocation();
  const client = useProtectedClient();

  const [requestReport, setRequestReport] = useState(false);

  const { mutate: sendInvites, isLoading } = useCreateClientInvitationMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      notify.info({ message: t('invites:inviteSuccess') });
    },
  });

  const payload: CreateClientInvitationInput = {
    department_id: '',
    location_id: '',
    email_addresses: checkedIds,
    subject: t('insights:subject', { clientTitle: client.title }),
    message: t('insights:message'),
    user_type: UserType.User,
  };

  let title = null;
  let report: ReportNames = null;
  let filter: OrganicInsightsReportFilter = null;
  switch (pathname) {
    case paths.insights:
    case paths.insightsCompanies:
      title = t('insights:companies_title');
      report = ReportNames.Companies;
      filter = { hide_company: (filters as CompaniesFilter)?.hide_company };
      break;
    case paths.insightsPeople:
      title = t('insights:people_title');
      filter = { hide_teammates: (filters as PeopleFilter)?.hide_teammates };
      if ((filters as PeopleFilter)?.ordering === Ordering.Top) {
        report = ReportNames.PeopleTop;
        break;
      } else {
        report = ReportNames.PeopleNewest;
        break;
      }
  }

  useGetOrganicInsightsReportQuery(
    {
      start: moment(filters?.start).unix(),
      end: moment(filters?.end).unix(),
      report,
      organic_insights_report_filter: filter,
      advanced_filters: advanced_filters,
    },
    {
      enabled: requestReport,
      onError: () => notify.error({ message: t('analytics:reportRequestedError') }),
      onSettled: () => setRequestReport(false),
    }
  );

  function onClickExportData() {
    notify.info({ message: t('analytics:reportRequested') });
    setRequestReport(true);
  }

  function onClickSend() {
    sendInvites({ data: payload });
  }

  function renderButtons() {
    if (registration_status === RegistrationStatus.Unregistered && checkedIds.length) {
      return (
        <Grid container alignItems="center" spacing={1}>
          <Grid>
            <Button
              size="large"
              variant="contained"
              startIcon={<SendIcon />}
              onClick={onClickSend}
              disabled={isLoading}
            >
              {t('invites:send')}
            </Button>
          </Grid>
        </Grid>
      );
    }

    return <ExportData onClick={onClickExportData} />;
  }

  return (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid xs>
        <Typography variant="h5" color="textSecondary">
          {title}
        </Typography>
      </Grid>

      <Grid>{renderButtons()}</Grid>
    </Grid>
  );
};

export default Header;
